import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { fetchAuthorBySlug, fetchSettings } from "@/lib/api";
import { ArticleCard } from "@/components/article-card";
import { Instagram, Twitter, Facebook, Globe, User as UserIcon, BookOpen } from "lucide-react";
import { getImageProps, getImageUrl } from "@/lib/utils";
import { useState, useMemo } from "react";

export const Route = createFileRoute("/penulis/$slug")({
  loader: async ({ params, context: { queryClient } }) => {
    try {
      const { slug } = params;
      const [author, settings] = await Promise.all([
        queryClient.ensureQueryData({
          queryKey: ["author", slug],
          queryFn: () => fetchAuthorBySlug(slug),
        }),
        queryClient.ensureQueryData({
          queryKey: ["settings"],
          queryFn: fetchSettings,
        }),
      ]);
      return { author, settings };
    } catch (e) {
      throw notFound();
    }
  },
  head: ({ loaderData, params }) => {
    const data = loaderData as any;
    const author = data?.author;
    const settings = data?.settings;
    const siteName = settings?.site_name || "Lihar Aksara";
    const canonicalUrl = `https://liharaksara.web.id/penulis/${params.slug}`;
    
    if (!author) return { meta: [{ title: `Penulis tidak ditemukan — ${siteName}` }] };

    const title = `${author.name} — Penulis di ${siteName}`;
    const description = author.bio || `Profil dan karya ${author.name} di ${siteName}.`;

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": author.name,
      "description": description,
      "url": canonicalUrl,
      "image": getImageUrl(author.avatar)
    };

    return {
      meta: [
        { title: `${title}` },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "profile" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
      ],
    };
  },
  component: AuthorProfilePage,
});

function AuthorProfilePage() {
  const { slug } = Route.useParams();
  const { data: author } = useSuspenseQuery({
    queryKey: ["author", slug],
    queryFn: () => fetchAuthorBySlug(slug),
  }) as any;

  const [activeTab, setActiveTab] = useState("all");

  const socialLinks = useMemo(() => {
    if (typeof author.socialLinks === "string") {
      try {
        return JSON.parse(author.socialLinks);
      } catch (e) {
        return {};
      }
    }
    return author.socialLinks || {};
  }, [author.socialLinks]);

  const posts = author.posts || [];
  
  const isNovel = (p: any) => p.type === "NOVEL";
  const isCerpen = (p: any) => p.type === "SHORTSTORY" || p.categories?.some((c: any) => c.slug === "cerpen");
  const isPuisi = (p: any) => p.type === "POETRY" || p.categories?.some((c: any) => c.slug === "puisi");
  const isArtikel = (p: any) => p.type === "ARTICLE" && !isCerpen(p) && !isPuisi(p);

  const counts = {
    all: posts.length,
    novel: posts.filter(isNovel).length,
    artikel: posts.filter(isArtikel).length,
    cerpen: posts.filter(isCerpen).length,
    puisi: posts.filter(isPuisi).length,
  };

  const filteredPosts = posts.filter((p: any) => {
    if (activeTab === "all") return true;
    if (activeTab === "novel") return isNovel(p);
    if (activeTab === "artikel") return isArtikel(p);
    if (activeTab === "cerpen") return isCerpen(p);
    if (activeTab === "puisi") return isPuisi(p);
    return true;
  });

  const tabs = [
    { id: "all", label: "Semua Karya", count: counts.all },
    { id: "novel", label: "Novel", count: counts.novel },
    { id: "cerpen", label: "Cerpen", count: counts.cerpen },
    { id: "puisi", label: "Puisi", count: counts.puisi },
    { id: "artikel", label: "Artikel", count: counts.artikel },
  ].filter(tab => tab.id === "all" || tab.count > 0);

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header Profile Section */}
      <div className="bg-secondary/30 border-b border-border py-20">
        <div className="container-editorial">
          <div className="flex flex-col md:flex-row items-center gap-10">
            <div className="w-32 h-32 md:w-48 md:h-48 rounded-sm overflow-hidden border-4 border-background shadow-xl bg-muted shrink-0 rotate-2">
              {author.avatar ? (
                <img
                  src={getImageUrl(author.avatar)}
                  alt={author.name}
                  className="w-full h-full object-cover -rotate-2"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-accent/10 text-accent -rotate-2">
                  <UserIcon size={64} className=" " />
                </div>
              )}
            </div>

            <div className="flex-1 text-center md:text-left">
              <h1 className="font-logo text-4xl md:text-6xl font-bold tracking-tight mb-4 text-foreground">
                {author.name}
              </h1>
              <p className="font-serif text-lg md:text-xl text-muted-foreground italic leading-relaxed max-w-2xl mb-6">
                {author.bio || "Penulis yang mendedikasikan jemarinya untuk merangkai makna dalam kata."}
              </p>

              {author.quote && (
                <div className="mb-8 border-l-2 border-accent/30 pl-6 py-1 mx-auto md:mx-0 max-w-2xl">
                   <p className="font-serif text-xl md:text-2xl text-foreground italic leading-tight opacity-90">
                     "{author.quote}"
                   </p>
                </div>
              )}

              <div className="flex flex-wrap justify-center md:justify-start items-center gap-6 text-sm">
                <div className="flex items-center gap-2 px-4 py-2 bg-background rounded-full border border-border">
                   <BookOpen size={16} className="  text-accent" />
                   <span className="font-bold text-[10px] uppercase tracking-widest">{posts.length} Karya Terbit</span>
                </div>
                
                <div className="flex items-center gap-4">
                  {socialLinks.instagram && (
                    <a aria-label="Instagram" href={`https://instagram.com/${socialLinks.instagram}`} target="_blank" rel="noreferrer" className="p-2 hover:text-accent transition-colors">
                      <Instagram size={20} className=" " />
                    </a>
                  )}
                  {socialLinks.twitter && (
                    <a aria-label="Twitter" href={`https://twitter.com/${socialLinks.twitter}`} target="_blank" rel="noreferrer" className="p-2 hover:text-accent transition-colors">
                      <Twitter size={20} className=" " />
                    </a>
                  )}
                  {socialLinks.facebook && (
                    <a aria-label="Facebook" href={`https://facebook.com/${socialLinks.facebook}`} target="_blank" rel="noreferrer" className="p-2 hover:text-accent transition-colors">
                      <Facebook size={20} className=" " />
                    </a>
                  )}
                  {(socialLinks.website || author.website) && (
                    <a aria-label="Website" href={socialLinks.website || author.website} target="_blank" rel="noreferrer" className="p-2 hover:text-accent transition-colors">
                      <Globe size={20} className=" " />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container-editorial py-20">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-16 gap-8">
          <div className="flex items-center gap-4">
            <h2 className="font-serif text-3xl md:text-4xl font-medium">Arsip Karya</h2>
            <div className="h-px w-12 bg-accent/30" />
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-2.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-accent text-white shadow-lg shadow-accent/20"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {tab.label} <span className="ml-1 opacity-50">({tab.count})</span>
              </button>
            ))}
          </div>
        </div>

        {filteredPosts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {filteredPosts.map((post: any) => (
              <ArticleCard key={post.id} article={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-24 bg-muted/30 rounded-3xl border border-dashed border-border">
            <p className="font-serif text-xl text-muted-foreground">Belum ada karya dalam kategori ini.</p>
          </div>
        )}
      </div>
    </div>
  );
}
